/**
 * Builds schema.org FAQPage JSON-LD from the site's FAQ records.
 *
 * Answers come out of a rich-text editor, so they arrive as HTML. Google permits
 * a small subset of tags inside acceptedAnswer.text, but LLM crawlers extract
 * plain text far more reliably — so everything is flattened to readable text
 * with list and paragraph breaks preserved.
 *
 * Two record shapes exist in the API:
 *   { title, discription }   — home page and country FAQs
 *   { question, answer }     — university FAQs
 */

const NAMED_ENTITIES = {
  "&nbsp;": " ",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&ldquo;": "“",
  "&rdquo;": "”",
  "&lsquo;": "‘",
  "&rsquo;": "’",
  "&ndash;": "–",
  "&mdash;": "—",
  "&hellip;": "…",
  // Tuition and fee answers routinely carry currency and symbol entities
  "&pound;": "£",
  "&euro;": "€",
  "&yen;": "¥",
  "&cent;": "¢",
  "&deg;": "°",
  "&times;": "×",
  "&middot;": "·",
  "&bull;": "•",
  "&copy;": "©",
  "&reg;": "®",
  "&trade;": "™",
};

const decodeEntities = (value) => {
  let out = value;

  Object.entries(NAMED_ENTITIES).forEach(([entity, char]) => {
    out = out.replace(new RegExp(entity, "gi"), char);
  });

  out = out.replace(/&#(\d+);/g, (_match, code) =>
    String.fromCharCode(Number(code))
  );
  out = out.replace(/&#x([0-9a-f]+);/gi, (_match, code) =>
    String.fromCharCode(parseInt(code, 16))
  );

  // &amp; must be decoded last, otherwise "&amp;lt;" would collapse to "<"
  return out.replace(/&amp;/gi, "&");
};

export const htmlToText = (value) => {
  if (typeof value !== "string" || value === "") return "";

  return decodeEntities(
    value
      // Editors sometimes embed <style>/<script>; drop their contents entirely
      .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, "")
      .replace(/<li[^>]*>/gi, "\n• ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|ul|ol|h[1-6]|tr)>/gi, "\n")
      .replace(/<[^>]+>/g, "")
  )
    .replace(/\r/g, "")
    .replace(/[ \t ]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
};

/** Normalises either record shape into { question, answer }. */
export const normaliseFaqItems = (items = []) => {
  if (!Array.isArray(items)) return [];

  const seen = new Set();

  return items
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      const question = htmlToText(item.title ?? item.question ?? "");
      const answer = htmlToText(
        item.discription ?? item.description ?? item.answer ?? ""
      );

      if (!question || !answer) return null;
      if (item.disable === true) return null;

      // Same question twice in one FAQPage is invalid markup
      const key = question.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);

      return { question, answer };
    })
    .filter(Boolean);
};

/**
 * @param {object[]} items raw FAQ records from the API
 * @param {object} [options]
 * @param {string} [options.id] stable @id for the FAQPage node
 * @returns {object|null} JSON-LD, or null when there is nothing worth emitting
 */
export function buildFaqSchema(items, options = {}) {
  const faqs = normaliseFaqItems(items);
  if (faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    ...(options.id ? { "@id": options.id } : {}),
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
